// Auditoria do 1º dia útil (#5791) — as Secretarias do ciclo lado a lado, numa tela só.
//
// O contrato manda auditar no primeiro dia útil do mês seguinte. Antes disto era abrir uma tela por
// Secretaria e confiar na memória: aqui as cinco aparecem juntas, com o que ficou para trás em
// destaque, e o registro de quem auditou fica em cada competência — é ele que transforma a rotina
// em evidência numa glosa.
//
// As contas são de src/lib/auditoriaCicloAgrupamento.ts (puro, testado) e as datas da régua
// (src/lib/reguaPrazosAgrupamento.ts). Aqui só I/O e tela.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ClipboardCheck, FileText, Package, RefreshCw, Undo2,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { motivoArquivoRecusado } from '../../src/lib/maloteAgrupamento';
import { enviarAnexoNf, linkComprovanteMalote } from '../../src/lib/maloteAgrupamentoStorage';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import ConfirmModal from '../../src/components/ConfirmModal';
import { useAuth } from '../../src/contexts/AuthContext';
import {
  ITENS_AUDITORIA, montarItensAuditoria, montarLinhaAuditoria, resumirCiclo,
  type FechamentoAuditoria, type LinhaAuditoria,
} from '../../src/lib/auditoriaCicloAgrupamento';
import { montarRegua } from '../../src/lib/reguaPrazosAgrupamento';
import { ROTULO_SITUACAO_PAGAMENTO } from '../../src/lib/pagamentoRetencaoAgrupamento';
import type { AgrupamentoFaturamento } from '../../types';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const competenciaIso = (rotulo: string) => {
  const [mes, ano] = rotulo.split('/');
  const i = MESES.indexOf(mes);
  return i < 0 ? '' : `${ano}-${String(i + 1).padStart(2, '0')}-01`;
};
const rotuloCompetencia = (iso: string) => {
  const [ano, mes] = iso.split('-');
  return `${MESES[Number(mes) - 1]}/${ano}`;
};
const dataBr = (iso: string | null | undefined) => {
  const s = String(iso ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const [a, m, d] = s.split('-');
  return `${d}/${m}/${a}`;
};
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Últimas 12 competências, da mais recente para trás: o ciclo auditado é sempre o mês passado. */
function competenciasRecentes(): string[] {
  const hoje = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
}

/** Competência que a auditoria olha por padrão: a do mês anterior, que é a que foi emitida. */
function competenciaPadrao(): string {
  const d = new Date();
  const ant = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return `${ant.getFullYear()}-${String(ant.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function AuditoriaCicloAgrupamentos({ onMudou }: { onMudou?: () => void }) {
  const { profile } = useAuth();
  const [competencia, setCompetencia] = useState(competenciaPadrao());
  const [linhas, setLinhas] = useState<LinhaAuditoria[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Registro da auditoria de uma Secretaria.
  const [auditando, setAuditando] = useState<LinhaAuditoria | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [observacao, setObservacao] = useState('');

  // Registro da NF.
  const [registrandoNf, setRegistrandoNf] = useState<LinhaAuditoria | null>(null);
  const [nfNumero, setNfNumero] = useState('');
  const [nfValor, setNfValor] = useState('');
  const [nfData, setNfData] = useState('');
  const [nfArquivo, setNfArquivo] = useState<File | null>(null);

  // Desfazer auditoria.
  const [desfazendo, setDesfazendo] = useState<LinhaAuditoria | null>(null);
  const [motivo, setMotivo] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const [{ data: ags, error: erroAg }, { data: fechs, error: erroF }, { data: fer }] = await Promise.all([
        supabase.from('agrupamentos_faturamento').select('*').eq('ativo', true).order('nome'),
        supabase.from('agrupamento_fechamentos').select('*').eq('competencia', competencia),
        supabase.from('feriados').select('data, tipo')
          .gte('data', `${competencia.slice(0, 4)}-01-01`)
          .lte('data', `${Number(competencia.slice(0, 4)) + 1}-12-31`),
      ]);
      if (erroAg) throw erroAg;
      if (erroF) throw erroF;
      // Mesmo formato da régua: o cadastro acrescenta feriado e também suprime um que não vale.
      const feriados = {
        incluir: (fer ?? []).filter((f: any) => f.tipo === 'inclui').map((f: any) => String(f.data).slice(0, 10)),
        excluir: (fer ?? []).filter((f: any) => f.tipo === 'exclui').map((f: any) => String(f.data).slice(0, 10)),
      };
      const porAgrupamento = new Map((fechs ?? []).map((f: any) => [f.agrupamento_id, f]));

      const montadas = (ags ?? []).map((a: AgrupamentoFaturamento) => {
        const f: any = porAgrupamento.get(a.id) ?? null;
        // Mesma régua do acompanhamento mensal, com os ajustes manuais daquela competência.
        const regua = montarRegua({
          competencia,
          diaVencimento: a.dia_vencimento,
          hojeIso: hojeIso(),
          emitidoEm: f?.emitido_em ?? null,
          feriados,
          regra: {
            diaLimiteEmissao: (a as any).dia_limite_emissao,
            diaAuditoriaUtil: (a as any).dia_auditoria_util,
            diaMaloteUtil: (a as any).dia_malote_util,
            diasPagamentoEdital: (a as any).dias_pagamento_edital,
          },
          ajustes: {
            limiteEmissao: f?.limite_emissao_ajustado ?? null,
            auditoria: f?.auditoria_ajustada ?? null,
            vencimento: f?.vencimento_ajustado ?? null,
            malote: f?.malote_ajustado ?? null,
          },
        });
        const bruta: FechamentoAuditoria = {
          id: f?.id ?? 0,
          agrupamentoId: a.id,
          nome: a.nome,
          status: f?.status ?? 'aberto',
          totalVigor: f?.total_vigor ?? null,
          valorCobrado: f?.valor_cobrado ?? null,
          ucsNoGrupo: f?.ucs_no_grupo ?? null,
          ucsResolvidas: f?.ucs_resolvidas ?? null,
          faturaBoletoId: f?.fatura_boleto_id ?? null,
          linhaDigitavel: f?.linha_digitavel_boleto ?? null,
          emitidoEm: f?.emitido_em ?? null,
          nfNumero: f?.nf_numero ?? null,
          nfAnexoPath: f?.nf_anexo_path ?? null,
          maloteEntregueEm: f?.malote_entregue_em ?? null,
          situacaoPagamento: f?.situacao_pagamento ?? null,
          valorRecebido: f?.valor_recebido ?? null,
          auditadoEm: f?.auditado_em ?? null,
          estadoPrazo: regua?.estado ?? null,
          limiteMalote: regua?.malote ?? null,
          dataAuditoria: regua?.auditoria ?? null,
        };
        return montarLinhaAuditoria(bruta, hojeIso());
      });
      setLinhas(montadas);
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível carregar a auditoria do ciclo.');
    } finally {
      setCarregando(false);
    }
  }, [competencia]);

  useEffect(() => { void carregar(); }, [carregar]);

  const resumo = useMemo(() => resumirCiclo(linhas, hojeIso()), [linhas]);
  const podeGerir = profile?.role === 'adm' || profile?.role === 'gerente' || profile?.role === 'analista';

  const chamar = async (nome: string, args: Record<string, unknown>, aoConfirmar: string) => {
    setSalvando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc(nome, args);
      if (error) throw error;
      if ((data as any)?.ok !== true) throw new Error('O banco não confirmou a operação.');
      setAviso(aoConfirmar);
      await carregar();
      onMudou?.();
      return true;
    } catch (e: any) {
      setErro(e?.message || 'A ação falhou.');
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const registrarAuditoria = async () => {
    if (!auditando) return;
    const ok = await chamar('registrar_auditoria_fechamento', {
      p_fechamento_id: auditando.id,
      p_itens: montarItensAuditoria(marcados),
      p_observacao: observacao.trim() || null,
    }, `Auditoria de ${auditando.nome} registrada.`);
    if (ok) { setAuditando(null); setMarcados(new Set()); setObservacao(''); }
  };

  const registrarNf = async () => {
    if (!registrandoNf) return;
    if (nfArquivo) {
      const recusa = motivoArquivoRecusado(nfArquivo);
      if (recusa) { setErro(recusa); return; }
    }
    const valor = Number(nfValor.replace(/\./g, '').replace(',', '.'));
    let caminho: string | null = null;
    if (nfArquivo) {
      setSalvando(true);
      try {
        caminho = await enviarAnexoNf(registrandoNf.agrupamentoId, registrandoNf.id, nfArquivo);
      } catch (e: any) {
        setErro(e?.message || 'Não foi possível enviar o anexo da nota.');
        setSalvando(false);
        return;
      } finally {
        setSalvando(false);
      }
    }
    const ok = await chamar('registrar_nf_fechamento', {
      p_fechamento_id: registrandoNf.id,
      p_numero: nfNumero.trim(),
      p_valor: Number.isFinite(valor) && valor > 0 ? valor : null,
      p_emitida_em: nfData || null,
      p_anexo_path: caminho,
    }, `Nota fiscal de ${registrandoNf.nome} registrada.`);
    if (ok) { setRegistrandoNf(null); setNfNumero(''); setNfValor(''); setNfData(''); setNfArquivo(null); }
  };

  const abrirAnexoNf = async (caminho: string) => {
    try {
      window.open(await linkComprovanteMalote(caminho), '_blank', 'noopener');
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível abrir o anexo da nota.');
    }
  };

  const desfazerAuditoria = async () => {
    if (!desfazendo) return;
    const ok = await chamar('desfazer_auditoria_fechamento', {
      p_fechamento_id: desfazendo.id,
      p_motivo: motivo.trim(),
    }, `Auditoria de ${desfazendo.nome} desfeita.`);
    if (ok) { setDesfazendo(null); setMotivo(''); }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">Auditoria do ciclo</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            As Secretarias do mês lado a lado. O contrato manda auditar no 1º dia útil do mês seguinte
            {resumo.dataAuditoria ? `, que nesta competência é ${dataBr(resumo.dataAuditoria)}` : ''}.
          </p>
        </div>
        <label className="ml-auto text-xs font-semibold uppercase text-gray-400" htmlFor="aud-mes">Competência</label>
        <select id="aud-mes" value={competencia} onChange={e => setCompetencia(e.target.value)}
          className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-800">
          {competenciasRecentes().map(c => <option key={c} value={c}>{rotuloCompetencia(c)}</option>)}
        </select>
        <button type="button" onClick={() => void carregar()} disabled={carregando}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium disabled:opacity-60 dark:border-gray-700">
          <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {erro && (
        <p className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-200" role="alert">{erro}</p>
      )}
      {aviso && (
        <p className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200" role="status">{aviso}</p>
      )}

      {carregando ? (
        <div className="py-10"><LoadingSpinner /></div>
      ) : (
        <>
          <div className={`mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border p-3 text-sm ${
            resumo.cicloAuditado
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200'
              : resumo.atrasado
                ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
                : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200'}`}>
            {resumo.cicloAuditado ? <CheckCircle2 size={16} /> : <ClipboardCheck size={16} />}
            <span><strong>{resumo.auditadas}</strong> de {resumo.secretarias} Secretarias auditadas</span>
            <span><strong>{resumo.emitidas}</strong> emitidas</span>
            <span className={resumo.comImpedimento > 0 ? 'font-semibold' : ''}>
              <strong>{resumo.comImpedimento}</strong> com algo que impede fechar
            </span>
            {resumo.cicloAuditado && <span className="font-semibold">Ciclo auditado.</span>}
            {!resumo.cicloAuditado && resumo.atrasado && <span className="font-semibold">A auditoria deste ciclo está atrasada.</span>}
          </div>

          <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {linhas.map(l => (
              <article key={l.agrupamentoId}
                className={`rounded-2xl border p-3 ${
                  l.auditadoEm
                    ? 'border-emerald-300 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-900/10'
                    : l.impedimentos > 0
                      ? 'border-amber-300 bg-white dark:border-amber-800 dark:bg-gray-800/60'
                      : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/60'}`}>
                <header className="flex flex-wrap items-center gap-2">
                  <h3 className="font-bold text-gray-900 dark:text-white">{l.nome}</h3>
                  {l.auditadoEm ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-semibold text-white">
                      <CheckCircle2 size={12} /> Auditada {dataBr(l.auditadoEm)}
                    </span>
                  ) : (
                    <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                      {l.status === 'emitido' ? 'A auditar' : l.status === 'fechado' ? 'Fechada' : 'Aberta'}
                    </span>
                  )}
                </header>

                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-xs font-semibold uppercase text-gray-400">Valor</dt>
                  <dd className="text-gray-800 dark:text-gray-100">
                    {reais(Number(l.valorCobrado ?? l.totalVigor ?? 0))}
                    {Number(l.totalVigor ?? 0) <= 0 && <span className="ml-1 text-xs text-gray-500">sem boleto</span>}
                  </dd>
                  <dt className="text-xs font-semibold uppercase text-gray-400">UCs</dt>
                  <dd className="text-gray-800 dark:text-gray-100">{l.ucsResolvidas ?? 0} de {l.ucsNoGrupo ?? 0}</dd>
                  <dt className="text-xs font-semibold uppercase text-gray-400">Emissão</dt>
                  <dd className="text-gray-800 dark:text-gray-100">{l.emitidoEm ? dataBr(l.emitidoEm) : 'não emitida'}</dd>
                  <dt className="text-xs font-semibold uppercase text-gray-400">NF</dt>
                  <dd className="text-gray-800 dark:text-gray-100">
                    {l.nfNumero || '—'}
                    {l.nfAnexoPath && (
                      <button type="button" onClick={() => void abrirAnexoNf(l.nfAnexoPath!)}
                        className="ml-2 text-xs font-medium underline">ver anexo</button>
                    )}
                  </dd>
                  <dt className="text-xs font-semibold uppercase text-gray-400">Malote</dt>
                  <dd className="text-gray-800 dark:text-gray-100">
                    {l.maloteEntregueEm ? dataBr(l.maloteEntregueEm) : `até ${dataBr(l.limiteMalote)}`}
                  </dd>
                  {l.situacaoPagamento && (
                    <>
                      <dt className="text-xs font-semibold uppercase text-gray-400">Pagamento</dt>
                      <dd className="text-gray-800 dark:text-gray-100">
                        {ROTULO_SITUACAO_PAGAMENTO[l.situacaoPagamento]}
                        {l.valorRecebido !== null && <span className="ml-1 text-xs text-gray-500">{reais(Number(l.valorRecebido))}</span>}
                      </dd>
                    </>
                  )}
                </dl>

                {l.pendencias.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {l.pendencias.map(p => (
                      <li key={p.codigo} className={`flex items-start gap-1.5 text-xs ${
                        p.gravidade === 'impedimento'
                          ? 'font-medium text-amber-800 dark:text-amber-300'
                          : 'text-gray-600 dark:text-gray-300'}`}>
                        <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {p.texto}
                      </li>
                    ))}
                  </ul>
                )}

                {podeGerir && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {!l.auditadoEm && (
                      <button type="button" disabled={!l.podeAuditar || salvando}
                        title={l.motivoNaoAuditar ?? undefined}
                        onClick={() => { setAuditando(l); setMarcados(new Set()); setObservacao(''); setErro(null); }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                        <ClipboardCheck size={14} /> Registrar auditoria
                      </button>
                    )}
                    {l.auditadoEm && (
                      <button type="button" disabled={salvando}
                        onClick={() => { setDesfazendo(l); setMotivo(''); setErro(null); }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                        <Undo2 size={14} /> Desfazer auditoria
                      </button>
                    )}
                    {l.status === 'emitido' && (
                      <button type="button" disabled={salvando}
                        onClick={() => { setRegistrandoNf(l); setNfNumero(l.nfNumero ?? ''); setNfValor(''); setNfData(''); setNfArquivo(null); setErro(null); }}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800">
                        <FileText size={14} /> {l.nfNumero ? 'Corrigir NF' : 'Registrar NF'}
                      </button>
                    )}
                    {!l.maloteEntregueEm && l.status === 'emitido' && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Package size={13} /> malote se registra na aba do mês
                      </span>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>

          {linhas.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Nenhum agrupamento ativo para auditar.
            </p>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!auditando}
        onClose={() => { if (!salvando) { setAuditando(null); setMarcados(new Set()); setObservacao(''); } }}
        onConfirm={registrarAuditoria}
        title={auditando ? `Auditoria de ${rotuloCompetencia(competencia)} — ${auditando.nome}` : ''}
        message="Marque o que você conferiu. Fica registrado quem auditou e quando, e isso é o que serve de evidência depois."
        confirmLabel={salvando ? 'Registrando...' : 'Registrar auditoria'}
        loading={salvando}
      >
        <ul className="mb-3 space-y-1 text-left">
          {ITENS_AUDITORIA.map(i => (
            <li key={i.codigo}>
              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input type="checkbox" checked={marcados.has(i.codigo)}
                  onChange={e => setMarcados(prev => {
                    const n = new Set(prev);
                    if (e.target.checked) n.add(i.codigo); else n.delete(i.codigo);
                    return n;
                  })}
                  className="mt-0.5" />
                {i.texto}
              </label>
            </li>
          ))}
        </ul>
        {auditando && auditando.pendencias.length > 0 && (
          <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-left text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            Esta Secretaria tem {auditando.pendencias.length} ponto(s) em aberto. Auditar não os resolve —
            registre na observação o que foi decidido sobre cada um.
          </p>
        )}
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="aud-obs">Observação</label>
        <textarea id="aud-obs" value={observacao} onChange={e => setObservacao(e.target.value)} rows={3}
          placeholder="O que foi conferido, o que ficou pendente e o que foi combinado."
          className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-800" />
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!registrandoNf}
        onClose={() => { if (!salvando) { setRegistrandoNf(null); setNfNumero(''); setNfValor(''); setNfData(''); setNfArquivo(null); } }}
        onConfirm={registrarNf}
        title={registrandoNf ? `Nota fiscal de ${rotuloCompetencia(competencia)} — ${registrandoNf.nome}` : ''}
        message="Os 30 dias do edital correm da nota devidamente atestada, não da emissão do boleto."
        confirmLabel={salvando ? 'Registrando...' : 'Registrar NF'}
        loading={salvando}
        confirmDisabled={!nfNumero.trim()}
      >
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="nf-num">
          Número <span className="text-red-500">*</span>
        </label>
        <input id="nf-num" value={nfNumero} onChange={e => setNfNumero(e.target.value)}
          className="mb-2 w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-800" />
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="nf-val">Valor</label>
        <input id="nf-val" inputMode="decimal" value={nfValor} onChange={e => setNfValor(e.target.value)}
          placeholder={registrandoNf ? String(registrandoNf.valorCobrado ?? registrandoNf.totalVigor ?? 0) : ''}
          className="mb-2 w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-800" />
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="nf-data">Emitida em</label>
        <input id="nf-data" type="date" value={nfData} onChange={e => setNfData(e.target.value)}
          className="mb-2 w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-800" />
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="nf-arq">Anexo da nota (opcional)</label>
        <input id="nf-arq" type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
          onChange={e => setNfArquivo(e.target.files?.[0] ?? null)} className="w-full text-sm" />
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!desfazendo}
        onClose={() => { if (!salvando) { setDesfazendo(null); setMotivo(''); } }}
        onConfirm={desfazerAuditoria}
        title={desfazendo ? `Desfazer a auditoria — ${desfazendo.nome}` : ''}
        message="O carimbo sai e a Secretaria volta para a lista de a auditar. O motivo fica registrado."
        confirmLabel={salvando ? 'Desfazendo...' : 'Desfazer auditoria'}
        loading={salvando}
        confirmDisabled={!motivo.trim()}
        variant="danger"
      >
        <label className="mb-1 block text-left text-xs font-bold uppercase text-gray-400" htmlFor="aud-motivo">
          Motivo <span className="text-red-500">*</span>
        </label>
        <textarea id="aud-motivo" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
          className="w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-800" />
      </ConfirmModal>
    </div>
  );
}
